
import {defineComponent} from 'vue'
import {UIStateDto} from "@/dtos/UIStateDto";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import {useToast} from "vue-toastification";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {ValueEnum} from "@/api/enums/value-enum";
import PopupOverviewItem from "@/components/layouts/popups/PopupOverviewItem.vue";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {generateUUIDv4} from "@/helpers/functions/string";
import {CreatePopupRestDto, PopupOverviewRestDto, PopupOverviewResultsRestDto} from "@/api/pharma-cms-popup/models";
import {PopupOverviewResultsRestDtoModel} from "@/models/api/pharma-cms-popup/PopupOverviewResultsRestDtoModel";
import {PopupRestService} from "@/services/rest/cms-popup/PopupRestService";
import {CreatePopupRestDtoModel} from "@/models/api/pharma-cms-popup/CreatePopupRestDtoModel";
import BaseModalButton from "@/components/UI/Bootstrap/Modal/BaseModalButton.vue";
import InputText from "@/components/UI/InputText.vue";
import BaseModal from "@/components/UI/Bootstrap/Modal/BaseModal.vue";

export default defineComponent({
  name: "PopupOverview",
  components: {
    BaseModal,
    InputText,
    BaseModalButton,
    PopupOverviewItem,
    BaseContainer,
    BaseTitle,
    AlertError2,
  },
  computed: {
    getPopups(): PopupOverviewRestDto[] {
      if (!this.popups.results?.length) {
        return [];
      }

      return this.popups.results!;
    },
  },
  data: () => ({
    modalId: generateUUIDv4('modal-content-new-popup-') as string,
    newPopupName: '' as string,
    popups: PopupOverviewResultsRestDtoModel.createWithDefaults() as PopupOverviewResultsRestDto,
    toaster: useToast(),
    pageUI: UIStateDto.createWithDefaults(),
  }),
  mounted() {
    this.reloadContent();
  },
  methods: {
    async reloadContent(): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        this.popups = await PopupRestService.getInstance().getPopups();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    goToDetail(popupId: string): object {
      return {
        name: 'edit-popup-wysiwyg',
        params: {
          id: popupId,
        }
      };
    },
    async deletePopup(id: string): Promise<void> {
      const answer = window.confirm(this.$t('deleteConfirmation'));

      if (answer) {
        this.pageUI
          .setNotReady()
          .clearError();

        try {
          await PopupRestService.getInstance().deletePopup(id);

          await this.afterSave(this.$t('deletedSuccessfully'));
        } catch (exceptions: any) {
          this.setExceptions(exceptions);
        } finally {
          this.pageUI.setReady();
        }
      }
    },
    async updateStatus(id: string, status: ValueEnum): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        await PopupRestService.getInstance().updatePopupStatus({value: status}, id);
        PopupRestService.getInstance().popupsChanged();
        await this.afterSave();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    async createPopup(): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        const formRef = this.$refs.popupForm as any;
        if (formRef && formRef.validate) {
          const validationResult = await formRef.validate();

          if (!validationResult.valid) {
            return; // Don't close modal if validation fails
          }
        }

        const createDto: CreatePopupRestDto = CreatePopupRestDtoModel.createWithDefaults();
        createDto.name = this.newPopupName;
        await PopupRestService.getInstance().addPopup(createDto);
        await this.afterSave();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    clickCreate(): void {
      // First, blur the input field to remove focus
      const inputElement = document.getElementById('newPopupName');
      if (inputElement) {
        (inputElement as HTMLElement).blur();
      }

      // Use nextTick to ensure the blur happens before the click
      this.$nextTick(() => {
        const createButton = this.$refs.createButton as any;
        if (createButton && createButton.$el) {
          createButton.$el.click();
        }
      });
    },
    async afterSave(message?: string): Promise<void> {
      window.scrollTo(0, 0);
      await handleSavedSuccessfully(message);
      await this.reloadContent();
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
  }
})
